import { type ReactNode, useState } from 'react';
import { clockIn, compactCount, formatCount, shortStamp, type Zone } from '@/lib/format';
import { useWidth } from '@/lib/hooks';

export interface ChartSeries {
    key: string;
    label: string;
    color: string;
    values: readonly number[];
}

export interface TimeChartProps {
    // The start of each bucket, in ms.
    times: readonly number[];
    stepMs: number;
    series: readonly ChartSeries[];
    // Moments worth a line across the chart: deploys.
    markers?: ReadonlyArray<{ at: number; label: string }>;
    from: number;
    to: number;
    zone: Zone;
    height?: number;
}

const RIGHT = 44;
const AXIS = 18;
const TOP = 26;

// A bar with a rounded top (when it is the top of its stack), square at its baseline.
const barPath = (x: number, y: number, w: number, h: number, r: number): string => {
    if (h <= 0) {
        return '';
    }
    const radius = Math.min(r, w / 2, h);

    return `M${x},${y + h}V${y + radius}Q${x},${y} ${x + radius},${y}H${x + w - radius}Q${x + w},${y} ${x + w},${y + radius}V${y + h}Z`;
};

// Stacked bars over time, in SVG: a legend, markers for deploys, a tooltip with every series of the bucket.
export function TimeChart(props: TimeChartProps): ReactNode {
    const [ref, width] = useWidth<HTMLDivElement>();
    const [hover, setHover] = useState<number | null>(null);
    const height = props.height ?? 160;
    const plot = Math.max(1, width - RIGHT);
    const base = height - AXIS;
    const totals = props.times.map((_, index) => props.series.reduce((sum, series) => sum + (series.values[index] ?? 0), 0));
    const max = Math.max(1, ...totals);
    const span = Math.max(1, props.to - props.from);
    const xOf = (t: number): number => ((t - props.from) / span) * plot;
    const band = (props.stepMs / span) * plot;
    const barWidth = band >= 5 ? Math.min(18, band * 0.62) : Math.max(1, band - 1);
    const longRange = span > 24 * 3600_000;
    const labels = Array.from({ length: 5 }, (_, index) => props.from + (span * index) / 4);
    const hovered = hover === null ? null : { t: props.times[hover] as number, index: hover };
    // Deploys: a line each, and a pill with the commit where there is room for one.
    const markers: Array<{ at: number; label: string; x: number; pill: boolean }> = [];
    let lastPill = -Infinity;
    for (const marker of props.markers ?? []) {
        const x = xOf(marker.at);
        const pill = x - lastPill > 96 && x > 30 && x < plot - 30;
        if (pill) {
            lastPill = x;
        }
        markers.push({ ...marker, x, pill });
    }

    return (
        <div className="tchart" ref={ref}>
            <div className="tlegend">
                {props.series.map((series) => (
                    <span key={series.key}>
                        <i style={{ background: series.color }} />
                        {series.label}
                    </span>
                ))}
            </div>
            {width > 0 && (
                <svg
                    width={width}
                    height={height}
                    onMouseMove={(event) => {
                        const x = event.clientX - event.currentTarget.getBoundingClientRect().left;
                        const t = props.from + (x / plot) * span;
                        let best: number | null = null;
                        props.times.forEach((time, index) => {
                            if (t >= time && t < time + props.stepMs) {
                                best = index;
                            }
                        });
                        setHover(best);
                    }}
                    onMouseLeave={() => setHover(null)}
                >
                    {[0, 0.5, 1].map((share) => (
                        <line
                            key={share}
                            className={share === 0 ? 'grid base' : 'grid'}
                            x1={0}
                            x2={plot}
                            y1={Math.round(base - (base - TOP) * share) + 0.5}
                            y2={Math.round(base - (base - TOP) * share) + 0.5}
                        />
                    ))}
                    <text className="axis" x={plot + 8} y={TOP + 4}>
                        {compactCount(max)}
                    </text>
                    <text className="axis" x={plot + 8} y={base + 3}>
                        0
                    </text>
                    {hovered && <rect className="hcol" x={xOf(hovered.t)} y={TOP - 6} width={Math.max(6, band)} height={base - TOP + 6} rx={6} />}
                    {props.times.map((time, index) => {
                        let y = base;
                        const present = props.series.filter((series) => (series.values[index] ?? 0) > 0);
                        const x = xOf(time) + (band - barWidth) / 2;

                        return (
                            <g key={time} opacity={hover === null || hover === index ? 1 : 0.5}>
                                {present.map((series, position) => {
                                    const value = series.values[index] ?? 0;
                                    const h = Math.max(1.5, (value / max) * (base - TOP));
                                    const top = position === present.length - 1;
                                    const gap = position > 0 ? 2 : 0;
                                    y -= h + gap;

                                    return <path key={series.key} d={barPath(x, y, barWidth, h, top ? 3 : 0)} fill={series.color} />;
                                })}
                            </g>
                        );
                    })}
                    {markers.map((marker) => (
                        <g key={`${marker.at}-${marker.label}`} className="marker">
                            <line x1={marker.x} x2={marker.x} y1={marker.pill ? 14 : TOP - 4} y2={base} />
                            {marker.pill && (
                                <>
                                    <rect x={marker.x - 44} y={2} width={88} height={18} rx={9} />
                                    <text x={marker.x} y={15} textAnchor="middle">
                                        {marker.label.length > 15 ? `${marker.label.slice(0, 14)}…` : marker.label}
                                    </text>
                                </>
                            )}
                            <title>{marker.label}</title>
                        </g>
                    ))}
                    {labels.map((t, index) => (
                        <text key={t} className="axis" x={Math.min(plot - 30, Math.max(0, xOf(t) - (index === 0 ? 0 : 18)))} y={height - 2}>
                            {longRange ? shortStamp(t, props.zone) : clockIn(new Date(t), props.zone).slice(0, 5)}
                        </text>
                    ))}
                </svg>
            )}
            {hovered && (
                <div className="tip" style={{ left: Math.min(xOf(hovered.t) + 16, Math.max(0, width - 240)), top: 30 }}>
                    <b>{clockIn(new Date(hovered.t), props.zone)}</b>
                    {props.series.map((series) => (
                        <div key={series.key}>
                            <i className="tdot" style={{ background: series.color }} /> {series.label}{' '}
                            {formatCount(series.values[hovered.index] ?? 0)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
